// @flow
import * as React from 'react';
import {
  usePropsValidation,
  types as t,
} from '../../validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/index.js';
import { WistiaEmbed } from '../../InternalWistiaModal.js';
import { productColor } from '../../designSystem.js';
import { useValidateWistiaVideoOwnedByMathspace } from '../../useValidateWistiaVideoOwnedByMathspace.js';

export function INVWistiaVideo(props: {|
  videoId: string, // eg 'gk9w45pd5q'
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    INVWistiaVideo,
  );

  useValidateWistiaVideoOwnedByMathspace({
    performValidation: props._performValidation,
    failedValidation,
    addErrors,
    location: locations.videoId,
    videoId: props.videoId,
  });

  // Our aspect ratios are all over the shop (278 distinct aspect ratios 😱)
  // The most frequent aspect ratio (at time of writing) is 4:3.
  // See here for some analysis:
  // https://paper.dropbox.com/doc/Wistia-Video-Aspect-Ratio-Determination--BhWNRojL805v05axiESTRSoMAg-T0kXoXIM3zvIuBEPoHrLv
  const aspectRatio = 4 / 3;
  const width = 596;
  const height = width / aspectRatio; // 447
  const borderRadius = 8;

  const [isLoading, setIsLoading] = React.useState(true);

  // We show a loading state before the video is visually ready as otherwise
  // we get gross interstitial crap while Wistia is initializing. We enforce
  // a minimum loading state duration of 500ms to prevent quick flashes of
  // the loading state in cases where the load was too quick (loads are
  // ~100ms if you've already cached everything in memory — too slow
  // to avoid gross interstitial stuff, but too quick for hiding the
  // loading state immediately).
  const hasUnmounted = React.useRef(false);
  React.useEffect(() => () => void (hasUnmounted.current = true), []);
  const loadStartTime = React.useRef(Date.now()).current;
  const onReady = React.useCallback(() => {
    const minLoadingStateDuration = 500;
    const loadingDuration = Date.now() - loadStartTime;
    if (loadingDuration >= minLoadingStateDuration) {
      setIsLoading(false);
    } else {
      const timeout = minLoadingStateDuration - loadingDuration;
      setTimeout(() => {
        if (hasUnmounted.current) return;
        setIsLoading(false);
      }, timeout);
    }
  }, [loadStartTime]);

  // There is a compositing? bug in the Blink rendering engine (Chrome/Edge)
  // which produces aliasing artifacts on the rounded corners when compositing
  // a clipped media layer (the video) with a separate, non-transparent
  // background layer. If we have a video that perfectly fills the available
  // space, this bug leads to a pixelated black border on the rounded corners,
  // which looks awful. To avoid this bug, we only show the black background
  // if the video layout leads to a black fill around the video at-least equal
  // in size to the radius of the border. This ensures there is no
  // intersection between the media layer, and the clipping path.
  const [showBlackBackground, setShowBlackBackground] = React.useState(false);
  const onDeterminedVideoLayout = React.useCallback(
    (result) => {
      // If there is some corner case that causes us to not be able to determine
      // the layout, default to using the black background.
      if (result.outcome === 'failure') {
        setShowBlackBackground(true);
        return;
      }

      const { layout } = result;
      const isBlink = navigator.vendor === 'Google Inc.';
      if (isBlink) {
        // Only show the black background if there is a enough room to
        // avoid the compositing bug manifesting.
        const showBlackBackground =
          layout.horizontalGap > borderRadius ||
          layout.verticalGap > borderRadius;
        setShowBlackBackground(showBlackBackground);
      } else {
        // The compositing bug doesn't exist for other render engines AFAICT,
        // so always show the black background.
        setShowBlackBackground(true);
      }
    },
    [setShowBlackBackground],
  );
  if (failedValidation) return null;

  return (
    <div
      style={{
        position: 'relative',
        boxShadow: '0px 8.75px 49px rgba(62, 62, 76, 0.4)',
        borderRadius,
        overflow: 'hidden',
        background: showBlackBackground ? '#000' : 'transparent',
        // Ensure we shrink-wrap to the dimensions of the WistiaEmbed
        display: 'inline-block',
        margin: '32px 0',
      }}
    >
      <WistiaEmbed
        videoId={props.videoId}
        width={width}
        height={height}
        onReady={onReady}
        onDeterminedVideoLayout={onDeterminedVideoLayout}
      />
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: productColor.cloudburst,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: isLoading ? 1 : 0,
          pointerEvents: isLoading ? 'auto' : 'none',
          transition: 'opacity 300ms ease',
          color: productColor.white,
          fontWeight: 600,
        }}
      >
        Loading video...
      </div>
    </div>
  );
}
INVWistiaVideo.spec = {
  videoId: t.string,
};
